import { FC, ReactNode } from 'react';

import { FileTypes, MediaFile } from '@/api/shared.types.ts';

const EvidenceCard: FC<{
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}> = ({ onClick, children, className }) => (
  <div
    onClick={onClick}
    className={`w-full aspect-[16/11] rounded-xl shadow-md bg-gray-50 overflow-hidden cursor-pointer 
      hover:shadow-xl hover:scale-[1.03] hover:-translate-y-1 
      transition-shadow duration-1000 ease-out 
      flex items-center justify-center ${className}`}
  >
    {children}
  </div>
);

export const ImageEvidence: FC<{ file: MediaFile; onClick?: () => void }> = ({
  file,
  onClick,
}) => (
  <EvidenceCard onClick={onClick}>
    <img
      src={file.url}
      alt={file.name || 'evidence image'}
      className="w-full h-full object-cover"
    />
  </EvidenceCard>
);

export const VideoEvidence: FC<{ file: MediaFile; onClick?: () => void }> = ({
  file,
  onClick,
}) => (
  <EvidenceCard onClick={onClick} className="bg-black">
    <video
      src={file.url}
      controls
      className="w-full h-full object-cover"
      onClick={onClick}
    >
      Your browser does not support video playback.
    </video>
  </EvidenceCard>
);

export const PdfEvidence: FC<{ file: MediaFile; onClick?: () => void }> = ({
  file,
  onClick,
}) => (
  <EvidenceCard onClick={onClick}>
    <div className="flex flex-col items-center gap-2 px-3">
      <i className="pi pi-file text-gray-400 text-3xl" />
      <span className="text-sm font-medium text-gray-700">PDF Document</span>
      <span className="text-xs text-gray-500 text-center truncate w-48">
        {file.name || 'Certificate.pdf'}
      </span>
    </div>
  </EvidenceCard>
);

const EvidencePreviewItem: FC<{
  file: MediaFile;
  index: number;
  onSelect: (index: number) => void;
}> = ({ file, index, onSelect }) => {
  const handleClick = (): void => onSelect(index);

  if (file.type === FileTypes.IMAGE) {
    return <ImageEvidence key={file.id} file={file} onClick={handleClick} />;
  }

  if (file.type === FileTypes.VIDEO) {
    return <VideoEvidence key={file.id} file={file} onClick={handleClick} />;
  }

  if (file.type === FileTypes.PDF) {
    return <PdfEvidence key={file.id} file={file} onClick={handleClick} />;
  }

  return (
    <EvidenceCard key={file.id}>
      <span className="text-sm text-gray-500">Unsupported type</span>
    </EvidenceCard>
  );
};

const MediaEvidencePreview: FC<{
  files: MediaFile[];
  onSelect: (index: number) => void;
}> = ({ files, onSelect }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {files.map((file, idx) => (
        <EvidencePreviewItem
          key={file.id}
          file={file}
          index={idx}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

export default MediaEvidencePreview;
